import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import compact from 'lodash/compact'
import { RugFormDataState } from 'pages/reporting/reports/rug/rugReportData'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { GeckoFileCategory, RolodexEntryType } from 'types'
import { fetchImageAsBase64, PDF_JPEG_ENCODING } from 'utils/imageUtils'

export interface AssetPicture {
  imagePath: string
  pdfSrc: string
  imageCaption?: string
}

export const useAssetPictures = (formData: RugFormDataState | undefined) => {
  // Treat the selected IDs as content-stable so unrelated form rerenders
  // do not restart image conversion work for the same selection.
  const reportIdsKey = (formData?.imagesSectionData.imageReportIds ?? []).join(
    '|'
  )
  const reportIds = useMemo(() => {
    return reportIdsKey ? reportIdsKey.split('|') : []
  }, [reportIdsKey])

  const {
    reports,
    isLoading: isLoadingReports,
    forceRefresh: forceRefreshReports,
  } = useRolodexEntries(
    reportIds.length > 0
      ? [
          {
            type: RolodexEntryType.REPORT,
            ids: reportIds,
          },
        ]
      : undefined
  )

  const [assetPictures, setAssetPictures] = useState<AssetPicture[]>([])
  const [isConverting, setIsConverting] = useState(false)

  const reportsById = useMemo(() => {
    return new Map(reports.map((report) => [report.id, report]))
  }, [reports])

  const photoFilePaths = useMemo(() => {
    return reportIds
      .map((reportId) => {
        const report = reportsById.get(reportId)

        if (!report || report.data.file_category !== GeckoFileCategory.IMAGE) {
          return undefined
        }

        return {
          entry_id: report.id,
          path: report.id,
        }
      })
      .filter(
        (
          filePath
        ): filePath is {
          entry_id: string
          path: string
        } => filePath !== undefined
      )
  }, [reportIds, reportsById])

  const {
    results: photoSignedUrls,
    isLoading: isLoadingSignedUrls,
    forceRefresh: forceRefreshPhotoSignedUrls,
  } = useRolodexSignedURLs(photoFilePaths)

  const signedUrlByReportId = useMemo(() => {
    return new Map(
      photoSignedUrls.map((result) => [result.entry_id, result.signed_url])
    )
  }, [photoSignedUrls])

  useEffect(() => {
    if (reportIds.length === 0) {
      setAssetPictures([])
      setIsConverting(false)
      return
    }

    if (isLoadingReports || isLoadingSignedUrls) {
      setIsConverting(true)
      return
    }

    const abortController = new AbortController()

    const preparePictures = async () => {
      setIsConverting(true)

      const nextAssetPictures = compact(
        await Promise.all(
          reportIds.map(async (reportId) => {
            const report = reportsById.get(reportId)

            if (
              !report ||
              report.data.file_category !== GeckoFileCategory.IMAGE
            ) {
              return undefined
            }

            const signedUrl = signedUrlByReportId.get(reportId)

            if (!signedUrl) {
              return undefined
            }

            try {
              return {
                imagePath: report.id,
                pdfSrc: await fetchImageAsBase64(
                  signedUrl,
                  abortController.signal,
                  undefined,
                  PDF_JPEG_ENCODING
                ),
                imageCaption: report.data.file_caption,
              }
            } catch {
              return undefined
            }
          })
        )
      )

      if (!abortController.signal.aborted) {
        setAssetPictures(nextAssetPictures)
        setIsConverting(false)
      }
    }

    preparePictures()

    return () => {
      abortController.abort()
    }
  }, [
    reportIds,
    reportsById,
    signedUrlByReportId,
    isLoadingReports,
    isLoadingSignedUrls,
  ])

  const forceRefresh = useCallback(async () => {
    await forceRefreshReports()
    await forceRefreshPhotoSignedUrls()
  }, [forceRefreshReports, forceRefreshPhotoSignedUrls])

  return {
    assetPictures,
    isLoading:
      reportIds.length > 0 &&
      (isLoadingReports || isLoadingSignedUrls || isConverting),
    forceRefresh,
  }
}
